import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Shield,
  RefreshCw,
  LogOut,
  Mail,
  Phone,
  Clock,
  Briefcase,
  Download,
  FileSpreadsheet,
  FileText,
  Eye,
  EyeOff,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gf_admin_token";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function toCSV(rows, headers) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = headers.map((h) => esc(h.label)).join(",");
  const body = rows
    .map((r) => headers.map((h) => esc(r[h.key])).join(","))
    .join("\n");
  return `${head}\n${body}`;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [draft, setDraft] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ consultation: [], recruitment: [] });
  const [tab, setTab] = useState("consultation");

  const loadLeads = useCallback(
    async (t) => {
      const useTok = t || token;
      if (!useTok) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${useTok}` },
        });
        setData({
          consultation: res.data?.consultation || [],
          recruitment: res.data?.recruitment || [],
        });
      } catch (e) {
        if (e?.response?.status === 401) {
          toast.error("Invalid password. Please log in again.");
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          setAuthed(false);
        } else {
          toast.error("Failed to load leads.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await axios.post(`${API}/admin/verify`, null, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAuthed(true);
        await loadLeads(token);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      }
    })();
  }, [token, loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    try {
      await axios.post(`${API}/admin/verify`, null, {
        headers: { Authorization: `Bearer ${draft.trim()}` },
      });
      localStorage.setItem(TOKEN_KEY, draft.trim());
      setToken(draft.trim());
      setAuthed(true);
      setDraft("");
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid password.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setData({ consultation: [], recruitment: [] });
  };

  const getExportSpec = () => {
    if (tab === "consultation") {
      return {
        rows: data.consultation,
        title: "Green Financials – Consultation Leads",
        filenameBase: `green-financials-consultation-${Date.now()}`,
        headers: [
          { key: "_index", label: "#" },
          { key: "created_at", label: "Date" },
          { key: "first_name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "best_time_to_call", label: "Best Time" },
        ],
      };
    }
    return {
      rows: data.recruitment,
      title: "Green Financials – Recruitment Leads",
      filenameBase: `green-financials-recruitment-${Date.now()}`,
      headers: [
        { key: "_index", label: "#" },
        { key: "created_at", label: "Date" },
        { key: "first_name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "current_occupation", label: "Occupation" },
      ],
    };
  };

  const decorate = (rows) =>
    rows.map((r, i) => ({
      ...r,
      _index: i + 1,
      created_at: fmtDate(r.created_at),
    }));

  const exportCSV = () => {
    const spec = getExportSpec();
    const rows = decorate(spec.rows);
    download(`${spec.filenameBase}.csv`, toCSV(rows, spec.headers));
  };

  const exportExcel = () => {
    const spec = getExportSpec();
    const rows = decorate(spec.rows).map((r) => {
      const obj = {};
      spec.headers.forEach((h) => {
        obj[h.label] = r[h.key] ?? "";
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab === "consultation" ? "Consultation" : "Recruitment");
    XLSX.writeFile(wb, `${spec.filenameBase}.xlsx`);
  };

  const exportPDF = () => {
    const spec = getExportSpec();
    const rows = decorate(spec.rows);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    // Title
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 70, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(spec.title, 40, 38);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(180, 180, 180);
    doc.text(
      `Exported: ${new Date().toLocaleString()}   ·   ${rows.length} record${rows.length === 1 ? "" : "s"}`,
      40,
      58
    );

    autoTable(doc, {
      startY: 90,
      head: [spec.headers.map((h) => h.label)],
      body: rows.map((r) => spec.headers.map((h) => String(r[h.key] ?? ""))),
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 8,
        textColor: [40, 40, 40],
      },
      headStyles: {
        fillColor: [0, 98, 65],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 247, 246] },
      margin: { left: 30, right: 30 },
    });

    doc.save(`${spec.filenameBase}.pdf`);
  };

  // ---------- LOGIN VIEW ----------
  if (!authed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl glass p-10"
        >
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-rolex/10 border border-rolex/30 mb-6">
            <Shield className="h-6 w-6 text-rolex-light" />
          </div>
          <div className="font-serif text-3xl text-white">Admin Access</div>
          <p className="text-sm text-gray-500 mt-2 font-light">
            Enter your password to view submitted leads.
          </p>
          <label className="block text-[11px] uppercase tracking-[0.28em] text-gray-500 mt-8 mb-3">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-token-input"
              autoFocus
              className="w-full bg-white/[0.03] border border-white/10 focus:border-rolex focus:bg-white/[0.05] focus:outline-none rounded-2xl pl-5 pr-14 py-4 text-white placeholder:text-gray-600 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              data-testid="admin-password-toggle"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-xl text-gray-500 hover:text-rolex-light hover:bg-white/[0.04] transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#006241] hover:bg-[#0a8a5e] text-white px-8 py-4 rounded-full text-[15px] font-medium transition-all duration-300 shadow-[0_0_40px_rgba(0,98,65,0.35)]"
          >
            Unlock Dashboard
          </button>
          <a
            href="/"
            className="block mt-6 text-center text-xs text-gray-500 hover:text-white"
          >
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  // ---------- DASHBOARD VIEW ----------
  const rows = data[tab] || [];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="border-b border-white/5 backdrop-blur-xl bg-black/60 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-rolex-light" />
            <div>
              <div className="text-sm font-medium">Green Financials</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-gray-500">
                Lead Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadLeads()}
              disabled={loading}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-white/30 text-sm text-gray-300 hover:text-white transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={exportCSV}
              data-testid="admin-export-csv-button"
              title="Download as CSV"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-white/30 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
            <button
              onClick={exportExcel}
              data-testid="admin-export-excel-button"
              title="Download as Excel"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-white/30 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Excel
            </button>
            <button
              onClick={exportPDF}
              data-testid="admin-export-pdf-button"
              title="Download as PDF"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-white/30 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <FileText className="h-3.5 w-3.5" />
              PDF
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] hover:bg-white/10 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-serif text-4xl md:text-5xl font-light">Leads</h1>
        <p className="text-sm text-gray-500 mt-2 font-light">
          Submissions from the website. Click refresh to fetch the latest.
        </p>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-white/5">
          {[
            { key: "consultation", label: "Consultation", count: data.consultation.length },
            { key: "recruitment", label: "Recruitment", count: data.recruitment.length },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 text-sm transition-colors relative -mb-px border-b-2 ${
                tab === t.key
                  ? "text-white border-rolex"
                  : "text-gray-500 border-transparent hover:text-gray-300"
              }`}
            >
              {t.label}
              <span className="ml-2 text-xs text-gray-500">({t.count})</span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-8 rounded-3xl border border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`admin-table-${tab}`}>
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.22em] text-gray-500 bg-white/[0.02]">
                  <th className="px-6 py-4 font-normal w-12">#</th>
                  <th className="px-6 py-4 font-normal">
                    <Clock className="inline h-3.5 w-3.5 mr-2" />
                    Date
                  </th>
                  <th className="px-6 py-4 font-normal">Name</th>
                  <th className="px-6 py-4 font-normal">
                    <Mail className="inline h-3.5 w-3.5 mr-2" />
                    Email
                  </th>
                  <th className="px-6 py-4 font-normal">
                    <Phone className="inline h-3.5 w-3.5 mr-2" />
                    Phone
                  </th>
                  <th className="px-6 py-4 font-normal">
                    {tab === "consultation" ? (
                      <>
                        <Clock className="inline h-3.5 w-3.5 mr-2" />
                        Best Time
                      </>
                    ) : (
                      <>
                        <Briefcase className="inline h-3.5 w-3.5 mr-2" />
                        Occupation
                      </>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-20 text-center text-gray-500 font-light"
                    >
                      {loading ? "Loading…" : "No leads yet."}
                    </td>
                  </tr>
                )}
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-6 py-4 text-gray-500 tabular-nums">{i + 1}</td>
                    <td className="px-6 py-4 text-gray-400">{fmtDate(r.created_at)}</td>
                    <td className="px-6 py-4 text-white">{r.first_name}</td>
                    <td className="px-6 py-4">
                      <a
                        href={`mailto:${r.email}`}
                        className="text-rolex-light hover:text-white transition-colors"
                      >
                        {r.email}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={`tel:${r.phone}`}
                        className="text-gray-300 hover:text-white transition-colors"
                      >
                        {r.phone}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {tab === "consultation"
                        ? r.best_time_to_call || "—"
                        : r.current_occupation || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
